/**
 * useExportWizard - Hook de gestión de estado para exportación Step3
 *
 * F5.04b2: Conecta Step3Page → ExportService (GeoJSON/CSV/KML/SHP/HTML)
 *
 * Responsabilidades:
 * - Convierte ProcessedInfrastructure[] → ProcessedRecord[]
 * - Gestiona selección de formatos y registros
 * - Ejecuta exportación vía ExportService
 * - Maneja descarga de archivos generados
 *
 * @version 1.0.0
 * @date 2026-02-24
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { ProcessedInfrastructure } from '@/types/processing';
import type { ResultadoCotejo } from '@/services/cotejo/types';
import type {
  ExportFormat,
  ExportOptions,
  ExportResult,
  ProcessedRecord,
  DocumentMetadata,
} from '@/types/export.types';
import { toProcessedRecords, mapTipologia } from '@/services/export/toProcessedRecord';
import { ExportService } from '@/services/export/ExportService';
import { buildSchematicMapDataUrl } from '@/services/export/SchematicMapBuilder';
import { getBoundaryByINE } from '@/services/boundaries';
import { getCatastroDataService } from '@/services/data/CatastroDataService';
import { infraTipologiaAPTEL, tipologiaACodigoCatastro } from '@/types/catastroR2';
import { enrichCatastroTooltip as _enrichCatastroTooltip } from '@/services/catastro/catastroEnricher';
import { useIsMounted } from '@/hooks/useIsMounted';
import { getOpenRTAR2Service, type ConsultaOpenRTA } from '@/services/geocoding/specialized/OpenRTAR2Service';
import { plazasRegistroPorFila, esAlojamientoPorNombre, resumirAlojamientosRTA } from '@/lib/albergue/alojamientosRTA';

// ============================================================================
// TIPOS
// ============================================================================

export interface UseExportWizardProps {
  /** Infraestructuras procesadas por el pipeline */
  infrastructures: ProcessedInfrastructure[];
  /** Metadatos del documento fuente */
  metadata: DocumentMetadata;
  /** Resultado del cotejo de completitud (P3); opcional → informe HTML con sección de cotejo (INF-1b) */
  resultadoCotejo?: ResultadoCotejo;
}

export type ExportPhase = 'idle' | 'validating' | 'exporting' | 'downloading' | 'done' | 'error';

export interface ExportState {
  /** Formatos seleccionados para exportar */
  selectedFormats: ExportFormat[];
  /** IDs de registros seleccionados */
  selectedIds: Set<number>;
  /** Fase actual de la exportación */
  phase: ExportPhase;
  /** Progreso (0-100) durante exportación */
  progress: number;
  /** Resultados de exportación */
  results: ExportResult[];
  /** Errores de validación */
  validationErrors: string[];
  /** Último error */
  error: string | null;
}

export interface UseExportWizardReturn {
  /** Registros procesados convertidos */
  records: ProcessedRecord[];
  /** Estado de exportación */
  state: ExportState;
  /** Registros seleccionados filtrados */
  selectedRecords: ProcessedRecord[];
  /** Total de registros geolocalizados */
  geoCount: number;
  /** Toggle de formato individual */
  toggleFormat: (format: ExportFormat) => void;
  /** Establecer formatos seleccionados */
  setFormats: (formats: ExportFormat[]) => void;
  /** Toggle de registro individual */
  toggleRecord: (id: number) => void;
  /** Seleccionar/deseleccionar todos */
  toggleAll: () => void;
  /** Ejecutar exportación completa */
  exportAll: () => Promise<void>;
  /** Descargar un resultado individual */
  downloadResult: (result: ExportResult) => void;
  /** Reiniciar estado */
  reset: () => void;
}

// ============================================================================
// FORMATOS POR DEFECTO
// ============================================================================

const DEFAULT_FORMATS: ExportFormat[] = ['xlsx'];

// ============================================================================
// HOOK
// ============================================================================

/**
 * PAR1 · eslabón 3 — ¿este elemento necesita enriquecimiento catastral por cercanía?
 *
 * Extraída del `useEffect` para poder comprobarla: es la condición que decide si
 * los eslabones 1 y 2 sirven de algo. El `!i.catastroParcel` es lo nuevo — sin
 * él, la parcela atada por refcat/dirección/nombre se sobrescribía con la del
 * vecino más próximo justo antes de exportar.
 */
export function needsCatastroEnrichment(i: ProcessedInfrastructure): boolean {
  return (i.score ?? 0) >= 60 && !!i.xFinal && !!i.yFinal && !i.catastroParcel;
}

/**
 * ATPX · eslabón 1 — códigos de uso DGC con los que filtrar el atado por cercanía.
 *
 * La tipología viaja hasta aquí en `geocoding.classification.type`
 * (`InfrastructureType`), **ya en el vocabulario `TipologiaPTEL`**: el enumerado
 * legacy declara sus valores 1:1 con las 19 macro de `TUN1` (`POLICE = 'POLICIAL'`,
 * `EMERGENCY = 'EMERGENCIAS'`). Por eso `infraTipologiaAPTEL` resuelve por su rama
 * `directa` en un solo salto y **no hace falta pasar por `mapTypeToTipologia`** —
 * el rodeo por el vocabulario DERA es precisamente lo que apagaba el filtro en el
 * orquestador (`V-037`, `FUS1`).
 *
 * `P4ZIP` (7-oct-2026): si la geolocalización no dejó tipología con discriminador
 * —la fila agotó el tiempo y el orquestador devolvió `GENERIC`, o no hay resultado—,
 * se usa la tipología del documento con el mismo resolutor que la exportación
 * (`mapTipologia(tipo, nombre)`, la columna `TIPOLOGIA` del ZIP). Medido sobre tres
 * ZIP de Guadix: las cinco filas que perdían la parcela de un día a otro eran, ese
 * día, filas con «Timeout» y coordenada del documento (`AUDIT_P4ZIP.md` §2). La
 * regla de seguridad no cambia: sin discriminador por ninguna de las dos vías, `null`.
 *
 * @returns Los códigos con los que filtrar, o `null` si **hay que abstenerse**.
 *   Devolver `[]` sería lo contrario de abstenerse: aguas abajo un array vacío
 *   **desactiva** el filtro (`V-036`), que es el modo de fallo que esta sesión
 *   elimina. `GENERICO`, `OTRO`, `MAREMOTO` y los elementos sin clasificar caen
 *   aquí: sin discriminador semántico no se hereda nada.
 */
export function usosParaEnriquecimiento(i: ProcessedInfrastructure): string[] | null {
  const ptel = infraTipologiaAPTEL(i.geocoding?.classification?.type);
  const usos = ptel ? tipologiaACodigoCatastro(ptel) : [];
  if (usos.length > 0) return usos;
  // `P4ZIP`: la geolocalización no dio discriminador → tipología del documento.
  const usosDoc = tipologiaACodigoCatastro(mapTipologia(i.tipo ?? '', i.nombre ?? ''));
  return usosDoc.length > 0 ? usosDoc : null;
}

/**
 * Umbral del atado por cercanía, en metros. Derivado empíricamente en `CCB1` §7.4
 * sobre Colomera: todos los aciertos correctos ocurren a ≤27 m, a 100 m ya hay
 * 30-148 candidatas y a 200 m hasta 392. Sustituye a los 200 m sin procedencia
 * que `V-027` señaló. **No se re-litiga sin medición nueva.**
 */
const ENRIQUECIMIENTO_MAX_DIST_M = 50;

/** ALBRTA-b2c (DOP-2 a): nota de las filas con nombre de alojamiento si no hay registro consultado. */
export const NOTA_RTA_NO_CONSULTADO =
  'No se pudo consultar el Registro de Turismo de Andalucía al exportar: columnas RTA_* sin datos.';

/**
 * ALBRTA-b2c · pega a cada fila lo que el registro dice de ella (`plazasRegistroPorFila`, la
 * misma función que P2; DOP-1 b de `b2b`). Pieza pura: se comprueba sin el hook.
 * - Con registro: solo las filas con algo que decir llevan `registroRTA`.
 * - Sin registro en el municipio: nada.
 * - Cargando o sin red al exportar: la nota, solo en filas cuyo nombre dice alojamiento
 *   (lista estricta, como P2). La exportación no espera.
 */
export function conRegistroRTA(
  records: ProcessedRecord[],
  carga: { estado: 'cargando' } | ConsultaOpenRTA,
): ProcessedRecord[] {
  if (carga.estado === 'sin_registro') return records;
  if (carga.estado === 'con_registro') {
    if (!carga.datos.alojamientos.length) return records;
    const porId = plazasRegistroPorFila(carga.datos.alojamientos, records);
    if (!porId.size) return records;
    return records.map((r) => (porId.has(r.id) ? { ...r, registroRTA: porId.get(r.id) } : r));
  }
  return records.map((r) => (esAlojamientoPorNombre(r.nombre)
    ? { ...r, registroRTA: { inscripciones: [], plazasRegistro: null, avisos: [NOTA_RTA_NO_CONSULTADO] } }
    : r));
}

export function useExportWizard({
  infrastructures,
  metadata,
  resultadoCotejo,
}: UseExportWizardProps): UseExportWizardReturn {
  // --- Enriquecimiento catastral (fire-and-forget, no bloquea UI) ---
  // Un documento PTEL = un municipio → una sola llamada R2 por sesión.
  // Popula catastroParcel en registros con score ≥ 60 y coordenadas.
  const [enrichedInfras, setEnrichedInfras] = useState<ProcessedInfrastructure[]>(infrastructures);
  const cancelRef = useRef(false);
  // FLAK (2-sep-2026): guarda de montaje para los `setState` tras `await` de
  // `exportAll`. Cierra OBS-FT-USEEXPORTWIZARD-WINDOW-UNHANDLED-FLAKE (3 rojos de
  // CI con 0 tests fallidos por `ReferenceError: window is not defined` en el
  // `catch`). Patrón canónico `SSCA-FIX-0`; este hook quedó fuera del censo del
  // paraguas SSCA, que solo contó componentes. `enrichAsync` conserva `cancelRef`
  // porque además cubre la invalidación por cambio de `codigoINE` (DOP-FLAK-1-bis=α).
  const isMounted = useIsMounted();

  // Enriquecimiento catastral async (fire-and-forget)
  useEffect(() => {
    cancelRef.current = true;
    cancelRef.current = false;

    const ineCode = metadata.codigoINE;
    if (!ineCode) return;

    // PAR1 · eslabón 3: no pisar una parcela ya atada por vía catastral.
    // ATPX (02-ago): este enriquecimiento elegía el vecino más próximo sin filtro
    // de uso a <200 m, y `CCB1` lo midió sobre Colomera en **2 de 8**. Ahora busca
    // por el índice espacial filtrando por los usos de la tipología, a ≤50 m, y
    // **se abstiene** si no hay candidata compatible. Medido sobre el mismo corpus:
    // **2/8 → 7/8**, con 1/8 abstención legítima («Cerro Cauro», sin candidata).
    // Cuando la cascada ya
    // ató la parcela por refcat, polígono/parcela, dirección o nombre
    // (`applyGeocodingResult`), esa es mejor por construcción y ésta la
    // sobrescribía sin avisar, justo antes de exportar.
    //
    // ⚠️ El guardián es «no pisar nunca», deliberadamente grueso, y el motivo
    // está medido. La procedencia SÍ es distinguible —`matchType`, y el
    // `validationStatus: 'CADASTRAL_AUTHORITATIVE'` que el orquestador deriva de
    // `_catastroAutoritativo`—, pero afinarlo entregaría los emparejamientos por
    // nombre y por dirección al vecino más próximo, que `CCB1` midió en **2 de
    // 8**. El precedente `CATPP-BC` parte B no traslada: allí el rival era un
    // resultado previo fiable; aquí el rival es ese 2/8.
    //
    // Y la confianza no sirve para afinarlo: `searchByName` puntúa **posición en
    // el ranking de uFuzzy**, no parecido de cadena (`score = 1 - i*0.1`), así
    // que el ganador siempre sale a 85 diga lo que diga el emparejamiento. Ver
    // `V-030` y `V-031`. Decisión pendiente de medir el acierto de `searchByName`
    // sobre los mismos puntos con que se midió la proximidad.
    const highScore = infrastructures.filter(needsCatastroEnrichment);
    if (highScore.length === 0) return;

    const enrichAsync = async () => {
      try {
        const catService = getCatastroDataService();

        const enrichMap = new Map<string, ProcessedInfrastructure>();
        for (const inf of highScore) {
          if (cancelRef.current) return;

          // ATPX · eslabón 1: sin discriminador semántico no se busca.
          const usos = usosParaEnriquecimiento(inf);
          if (!usos) continue;

          const candidatas = await catService.searchByProximity(
            ineCode, inf.xFinal!, inf.yFinal!,
            { usos, maxDistance: ENRIQUECIMIENTO_MAX_DIST_M },
          );
          if (cancelRef.current) return;

          // ATPX · eslabón 2 — la regla de seguridad: sin candidata compatible
          // dentro del umbral, NO se hereda. Atar sin filtro es peor que no atar,
          // porque el dato equivocado no se distingue del bueno en el entregable
          // (`CCB1` §7.4). Los dos lectores toleran la ausencia: `toProcessedRecord`
          // omite las columnas catastrales y `MatchingEngine.scoreCatastral`
          // puntúa neutro. Heredar del vecino equivocado, no.
          if (candidatas.length === 0) continue;

          enrichMap.set(inf.id, { ...inf, catastroParcel: candidatas[0].parcel });
        }

        if (cancelRef.current || enrichMap.size === 0) return;
        setEnrichedInfras(prev =>
          prev.map(inf => enrichMap.has(inf.id) ? enrichMap.get(inf.id)! : inf),
        );
      } catch {
        // Fallo silencioso — R2 no disponible o timeout
      }
    };

    enrichAsync();
    return () => { cancelRef.current = true; };
  }, [infrastructures, metadata.codigoINE]);

  // --- Conversión de datos (memoizada, usa infras enriquecidas) ---
  // OBS-P1-PARSER-PLACEHOLDER-INDICAR (DOP-PLACEHOLDER-PUNTO-DE-FILTRO=A):
  // las filas plantilla «pendiente de rellenar» (esPlaceholder=true, F307) se
  // excluyen de los artefactos de export y de la lista de selección del paso 4.
  // El parser las conserva (balanceIngesta las contabiliza como placeholders),
  // por lo que INFADM y los recuentos de Step2 no se ven afectados.
  // ALBRTA-b2c · Registro de Turismo de Andalucía (OpenRTA): misma descarga y caché que P2
  // (`consultarMunicipio`), sin esperar (DOP-2 a): la exportación nunca se bloquea por él.
  const ineRTA = metadata.codigoINE;
  const [cargaRTA, setCargaRTA] = useState<{ estado: 'cargando' } | ConsultaOpenRTA>({ estado: 'cargando' });
  useEffect(() => {
    let vivo = true;
    setCargaRTA({ estado: 'cargando' });
    if (!ineRTA) { setCargaRTA({ estado: 'fallo' }); return; }
    getOpenRTAR2Service().consultarMunicipio(ineRTA)
      .then((c) => { if (vivo) setCargaRTA(c); })
      .catch(() => { if (vivo) setCargaRTA({ estado: 'fallo' }); });
    return () => { vivo = false; };
  }, [ineRTA]);

  const records = useMemo(() => {
    const base = toProcessedRecords(enrichedInfras.filter(i => !i.esPlaceholder));
    return conRegistroRTA(base, cargaRTA);
  }, [enrichedInfras, cargaRTA]);

  // ALBRTA-b2c-3b (DOP-1 a′): alojamientos que el plan no recoge, sobre TODAS las filas del
  // plan (como P3), no solo las marcadas. Sin registro, cargando o sin red: no hay bloque.
  const alojamientosRTA = useMemo(() => (cargaRTA.estado === 'con_registro'
    ? resumirAlojamientosRTA(cargaRTA.datos.alojamientos, cargaRTA.datos.viviendas, records)
    : undefined), [cargaRTA, records]);

  // --- Estado ---
  const [selectedFormats, setSelectedFormats] = useState<ExportFormat[]>(DEFAULT_FORMATS);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(records.map(r => r.id)),
  );
  const [phase, setPhase] = useState<ExportPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ExportResult[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // --- Derivados ---
  const selectedRecords = useMemo(
    () => records.filter(r => selectedIds.has(r.id)),
    [records, selectedIds],
  );

  const geoCount = useMemo(
    () => records.filter(r => r.geocodificado).length,
    [records],
  );

  // --- Handlers ---

  const toggleFormat = useCallback((format: ExportFormat) => {
    setSelectedFormats(prev =>
      prev.includes(format)
        ? prev.filter(f => f !== format)
        : [...prev, format],
    );
  }, []);

  const setFormats = useCallback((formats: ExportFormat[]) => {
    setSelectedFormats(formats);
  }, []);

  const toggleRecord = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === records.length) return new Set();
      return new Set(records.map(r => r.id));
    });
  }, [records]);

  const reset = useCallback(() => {
    setPhase('idle');
    setProgress(0);
    setResults([]);
    setValidationErrors([]);
    setError(null);
  }, []);

  const downloadResult = useCallback((result: ExportResult) => {
    if (result.success && result.blob.size > 0) {
      ExportService.downloadBlob(result.blob, result.filename);
    }
  }, []);

  // --- Exportación principal ---

  const exportAll = useCallback(async () => {
    // 1. Validar
    setPhase('validating');
    setError(null);
    setResults([]);

    // INF-5a: mapa esquemático del municipio (data-URL SVG) para el informe HTML.
    // El boundary se lee del cache caliente de P2 (getBoundaryByINE); si está frío,
    // no se encuentra o el INE es catastral (BL-04) → null → esquema solo-puntos.
    // Sin puntos válidos → cadena vacía → INF-5b omite la sección (consumidor inerte).
    const boundary = getBoundaryByINE(metadata.codigoINE);
    const mapaEstatico = buildSchematicMapDataUrl(selectedRecords, boundary);

    const options: ExportOptions = {
      formatos: selectedFormats,
      registros: selectedRecords,
      metadata,
      incluirResumen: true,
      incluirEstilos: true,
      resultadoCotejo,
      mapaEstatico,
      alojamientosRTA,
    };

    const errors = ExportService.validateOptions(options);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setPhase('error');
      setError(errors.join('; '));
      return;
    }

    setValidationErrors([]);

    // 2. Exportar
    setPhase('exporting');
    setProgress(0);

    try {
      const exportResults = await ExportService.exportAll(options);
      if (isMounted()) setResults(exportResults);

      const failed = exportResults.filter(r => !r.success);
      if (failed.length > 0 && isMounted()) {
        setError(`${failed.length} formato(s) con errores: ${failed.map(f => f.error).join('; ')}`);
      }

      // 3. Descargar exitosos
      if (isMounted()) setPhase('downloading');
      const successful = exportResults.filter(r => r.success);
      if (successful.length >= 2) {
        // ≥2 formatos exitosos → un único ZIP del lote. Los fallidos se excluyen
        // (su error ya se señaló arriba). El shapefile entra como una entrada más.
        const zipName = ExportService.buildLoteZipFilename(
          metadata.municipio,
          metadata.codigoINE
        );
        const zipBlob = await ExportService.packageBlobsToZip(successful);
        ExportService.downloadBlob(zipBlob, zipName);
      } else {
        // 1 formato exitoso → descarga directa (comportamiento actual).
        for (const result of successful) {
          ExportService.downloadBlob(result.blob, result.filename);
        }
      }

      if (!isMounted()) return;
      setProgress(100);
      setPhase(failed.length > 0 ? 'error' : 'done');
    } catch (err) {
      if (!isMounted()) return;
      setError(err instanceof Error ? err.message : 'Error inesperado durante exportación');
      setPhase('error');
    }
  }, [selectedFormats, selectedRecords, metadata, resultadoCotejo, alojamientosRTA, isMounted]);

  // --- Return ---
  return {
    records,
    state: {
      selectedFormats,
      selectedIds,
      phase,
      progress,
      results,
      validationErrors,
      error,
    },
    selectedRecords,
    geoCount,
    toggleFormat,
    setFormats,
    toggleRecord,
    toggleAll,
    exportAll,
    downloadResult,
    reset,
  };
}

export default useExportWizard;
